/* eslint-disable no-console, react/jsx-pascal-case, react/jsx-no-bind */
import classnames from 'classnames';
import React, { PropTypes, Component } from 'react';
import IME from 'es6/components/apps/ime';
import FitText from 'es6/components/common/fit-text';
import Glyph, { CORRECT_RESULT, INCORRECT_RESULT } from 'es6/components/common/glyph';
import { ResultTypes } from 'es6/models/quiz-result';
import { SPELL_CORRECT, SPELL_INCORRECT } from 'es6/core/sound-effects';
import SoundPlayer from 'es6/components/common/sound-player';
import SoundManager from 'es6/services/sound-manager';

// Spelling practice states.
const SPELLING_PRACTICE_DEFAULT = 0;
const SPELLING_PRACTICE_CORRECT = 1;
const SPELLING_PRACTICE_INCORRECT = 2;

// Time durations to disable the key input before rebuilding.
const DISABLE_INPUT_DURATION = 250;

// Duration to show the correct/incorrect icon before fading it out.
const ICON_VISIBLE_DURATION = 750;

const INITIAL_STATE = {
  mode: SPELLING_PRACTICE_DEFAULT,
  enableInputTimeout: null,
  hideIconTimeout: null,
  inputDisabled: false,
  result: null,
};

/**
 * Handles the text was confirmed result from the IME.
 *
 * @this SpellingPractice
 * @param {Object} result      results passed back from the IME KeyInput instance
 */
function handleTextWasConfirmed(result) {
  const enableInput = () => this.setState({ inputDisabled: false });
  const enableInputTimeout = setTimeout(enableInput, DISABLE_INPUT_DURATION);

  const hideIcon = () => {
    // If the answer was correct, also clear the input after the fade out delay.
    if (this.state.result === ResultTypes.CORRECT) {
      this.reset();
    }

    this.setState({ mode: SPELLING_PRACTICE_DEFAULT });
  };
  const hideIconTimeout = setTimeout(hideIcon, ICON_VISIBLE_DURATION);

  const newState = {
    inputDisabled: true,
    enableInputTimeout,
    hideIconTimeout,
    result: result.result,
  };

  if (result.result === ResultTypes.CORRECT) {
    SoundManager.playEffect(SPELL_CORRECT);
    newState.mode = SPELLING_PRACTICE_CORRECT;
  } else {
    SoundManager.playEffect(SPELL_INCORRECT);
    newState.mode = SPELLING_PRACTICE_INCORRECT;
  }

  this.setState(newState);
}

class SpellingPractice extends Component {
  constructor(props) {
    super(props);

    this.handleKeyDown = e => {
      if (this.state.inputDisabled) {
        console.error('IME triggered onKeyDown while disabled.');
      }

      // Clear the input if the user types something after an incorrect answer.
      if (this.state.result !== null) {
        this.reset();
      }
    };

    this.state = INITIAL_STATE;
  }

  componentWillUpdate(nextProps) {
    const { language, text } = this.props;

    if (nextProps.language !== language || nextProps.text !== text) {
      this.cancelTimeouts();
      this.setState(INITIAL_STATE);
    }
  }

  componentWillUnmount() {
    this.cancelTimeouts();
  }

  cancelTimeouts() {
    this.state.enableInputTimeout && clearTimeout(this.state.enableInputTimeout);
    this.state.hideIconTimeout && clearTimeout(this.state.hideIconTimeout);
  }

  reset() {
    this.refs.ime.rebuild();
    this.setState({ result: null });
  }

  render() {
    const { language, text, sound } = this.props;
    const { mode, inputDisabled } = this.state;

    return (
      <div className={classnames('spelling-practice', 'fill_in_the_blank')}>

        <Glyph type={CORRECT_RESULT} className={classnames('result', { reveal: mode === SPELLING_PRACTICE_CORRECT })} />
        <Glyph type={INCORRECT_RESULT} className={classnames('result', { reveal: mode === SPELLING_PRACTICE_INCORRECT })} />

        {sound && <SoundPlayer source={sound} />}

        <div className="ime-container">
          <FitText key={text}>
            <IME
              ref="ime"
              autoConfirm
              disabled={inputDisabled}
              language={language}
              onTextWasConfirmed={handleTextWasConfirmed.bind(this)}
              onKeyDown={this.handleKeyDown}
              text={text}
              correctLettersTypeable
            />
          </FitText>
        </div>
      </div>
    );
  }
}

SpellingPractice.propTypes = {
  language: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  sound: PropTypes.string,
};

export default SpellingPractice;
